import { useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Checkbox, Flex, Modal, Typography, theme } from 'antd';
import { ImportOutlined } from '@ant-design/icons';
import type {
  PublicationImportProductsIn,
  PublicationImportProductsOut,
} from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';
import { ProductImportResult } from './ProductImportResult';
import { BATCH_SIZE, batches, mergeResults } from './productImportOutcomes';

const { Text } = Typography;

interface ProductImportControlProps {
  /** Content-catalog product ids to import into the user catalog. */
  ids: number[];
  /** Content-catalog product titles by id, for the per-product outcome list. */
  titles?: Record<number, string>;
  /**
   * A run finished; the publication list has been invalidated. The modal
   * stays open — invalidate the caller's own list from here.
   */
  onDone?: () => void;
  /**
   * The user closed the modal after a finished run — the safe point to
   * clear the table selection (clearing it earlier would unmount this
   * control and the open result view with it).
   */
  onClosed?: () => void;
  /** The modal opened or closed; keep this control mounted while open. */
  onOpenChange?: (open: boolean) => void;
}

type Phase = 'confirm' | 'running' | 'done';

/**
 * Bulk "import these content-catalog products" for list views that show
 * product rows. Create-only: a product already bound to a local row comes
 * back reported, never updated — per-publication updates go through the
 * import-diff modal instead.
 */
export const ProductImportControl = ({
  ids,
  titles,
  onDone,
  onClosed,
  onOpenChange,
}: ProductImportControlProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation<PublicationImportProductsOut>();

  const [open, setOpen] = useState(false);
  // Off by default, as the API is: without it a title whose other format is
  // already local is refused (`fusion_not_allowed`) rather than merged into,
  // and merging two upstream publications into one local row is a decision
  // the admin should make deliberately.
  const [allowFusion, setAllowFusion] = useState(false);
  const [ignoreGate, setIgnoreGate] = useState(false);
  const [phase, setPhase] = useState<Phase>('confirm');
  const [result, setResult] = useState<PublicationImportProductsOut | null>(
    null
  );
  // The run acts on what was selected when it started: the refetch after a
  // run drops imported rows out of the "not imported" view, and a live `ids`
  // would shrink with it.
  const [runIds, setRunIds] = useState<number[]>([]);
  const [runTitles, setRunTitles] = useState<Record<number, string>>({});
  // Batches answered so far, for the progress line while the run goes.
  const [sent, setSent] = useState(0);

  const runBatches = batches(runIds);

  const tk = (suffix: string, options?: Record<string, unknown>) =>
    t(`user_catalog.publications.import_products.${suffix}`, options);

  const start = () => {
    setAllowFusion(false);
    setIgnoreGate(false);
    setPhase('confirm');
    setResult(null);
    setSent(0);
    setRunIds(ids);
    setRunTitles(titles ?? {});
    setOpen(true);
    onOpenChange?.(true);
  };

  const close = () => {
    if (phase === 'running') return;
    const finished = phase === 'done';
    setOpen(false);
    onOpenChange?.(false);
    if (finished) onClosed?.();
  };

  const importBatch = async (productIds: number[]) => {
    const payload: PublicationImportProductsIn = {
      product_ids: productIds,
      allow_fusion: allowFusion,
      ignore_distribution_gate: ignoreGate,
    };
    const { data } = await mutateAsync({
      url: 'user_catalog/publications/import_products',
      method: 'post',
      values: payload,
      successNotification: false,
      errorNotification: false,
    });
    return data;
  };

  const run = async () => {
    setPhase('running');
    setSent(0);
    const answered: PublicationImportProductsOut[] = [];
    try {
      for (const batch of runBatches) {
        answered.push(await importBatch(batch));
        setSent(answered.length);
      }
    } catch (error) {
      notify?.({
        type: 'error',
        message: tk('title'),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
      if (answered.length === 0) {
        // Nothing landed: back to the confirmation, so the user can retry
        // the whole selection or walk away.
        setPhase('confirm');
        return;
      }
      // Earlier batches are already committed. Show what they did rather
      // than dropping it, and say the run stopped short.
      notify?.({
        type: 'error',
        message: tk('title'),
        description: tk('partial', {
          done: answered.length,
          total: runBatches.length,
        }),
      });
    }
    setResult(mergeResults(answered));
    setPhase('done');
    invalidate({
      resource: 'user_catalog.publications',
      invalidates: ['list'],
    });
    onDone?.();
  };

  const running = phase === 'running';

  return (
    <>
      <Button
        type="primary"
        icon={<ImportOutlined />}
        disabled={ids.length === 0}
        onClick={start}
      >
        {tk('button')}
      </Button>

      <Modal
        open={open}
        title={tk('title')}
        closable={!running}
        keyboard={!running}
        maskClosable={false}
        onCancel={close}
        destroyOnClose
        width={720}
        footer={
          <Flex align="center" gap={token.marginSM}>
            <Text
              type="secondary"
              style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
            >
              {phase === 'done' && result
                ? tk('summary', {
                    imported: result.imported,
                    fused: result.fused,
                    skipped: result.skipped,
                    blocked: result.blocked,
                    failed: result.failed,
                  })
                : tk('confirm', { count: runIds.length })}
            </Text>
            {phase === 'done' ? (
              <Button type="primary" onClick={close}>
                {t('actions.close')}
              </Button>
            ) : (
              <>
                <Button onClick={close} disabled={running}>
                  {t('actions.cancel')}
                </Button>
                <Button type="primary" loading={running} onClick={run}>
                  {running ? tk('running') : tk('run')}
                </Button>
              </>
            )}
          </Flex>
        }
      >
        {phase === 'done' && result ? (
          <ProductImportResult result={result} titles={runTitles} />
        ) : (
          <Flex vertical gap={token.marginSM}>
            {runBatches.length > 1 && (
              <Text type="secondary">
                {tk('batched', {
                  batches: runBatches.length,
                  size: BATCH_SIZE,
                })}
              </Text>
            )}
            <Text type="secondary">{tk('create_only_hint')}</Text>
            <Checkbox
              checked={allowFusion}
              disabled={running}
              onChange={(e) => setAllowFusion(e.target.checked)}
            >
              {tk('allow_fusion')}
            </Checkbox>
            <Checkbox
              checked={ignoreGate}
              disabled={running}
              onChange={(e) => setIgnoreGate(e.target.checked)}
            >
              {tk('ignore_gate')}
            </Checkbox>
            {running && (
              <Text type="secondary">
                {runBatches.length > 1
                  ? tk('running_batch', {
                      done: sent,
                      total: runBatches.length,
                    })
                  : tk('running_hint')}
              </Text>
            )}
          </Flex>
        )}
      </Modal>
    </>
  );
};
